import { useEffect, useRef, useState } from "react";
import { Search as SearchIcon } from "lucide-react";
import { API, errMsg, TOPIC_NAMES } from "../lib/api";
import { PageHeader, DifficultyBadge, EmptyState } from "../components/bits";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";

const LETTERS = ["A", "B", "C", "D", "E"];

export default function Search() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [detail, setDetail] = useState(null);
  const timer = useRef(null);

  useEffect(() => {
    if (q.trim().length < 2) { setResults(null); return; }
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        const { data } = await API.get(`/search?q=${encodeURIComponent(q)}`);
        setResults(data);
      } catch (e) { toast.error(errMsg(e)); }
    }, 300);
    return () => clearTimeout(timer.current);
  }, [q]);

  const openDetail = async (id) => {
    try {
      const { data } = await API.get(`/questions/${id}/view`);
      setDetail(data);
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="search-page" className="max-w-3xl">
      <PageHeader title="Search the question bank" sub="Search by condition, symptom, drug, topic or question ID." />
      <div className="relative">
        <SearchIcon className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input data-testid="search-input" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="e.g. NSTEMI, nitrofurantoin, croup, PM-0001…" className="pl-11 py-6 text-base" autoFocus />
      </div>
      <div className="mt-6 space-y-3" data-testid="search-results">
        {results === null ? (
          <p className="text-sm text-muted-foreground py-10 text-center">Type at least 2 characters to search.</p>
        ) : results.length === 0 ? (
          <EmptyState title="No matches" body="Try a different term — the bank is growing every week." testid="search-empty" />
        ) : results.map((r) => (
          <button key={r.id} data-testid={`search-result-${r.qid}`} onClick={() => openDetail(r.id)}
            className="w-full text-left bg-card border border-border rounded-lg p-5 hover:border-primary/40 hover:shadow-sm transition-all">
            <div className="flex flex-wrap items-center gap-2 mb-2 text-xs text-muted-foreground">
              <span className="font-semibold text-primary">{r.qid}</span><span>·</span>
              <span>{TOPIC_NAMES[r.topic] || r.topic}</span>
              <DifficultyBadge level={r.difficulty} />
            </div>
            <p className="text-sm leading-relaxed line-clamp-2">{r.stem}</p>
          </button>
        ))}
      </div>

      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="search-detail-dialog">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="text-sm font-semibold text-muted-foreground">
                  {detail.question.qid} · {TOPIC_NAMES[detail.question.topic]} · attempted {detail.history.attempts}×
                </DialogTitle>
              </DialogHeader>
              <p className="text-base font-medium leading-relaxed">{detail.question.stem}</p>
              <div className="space-y-2 mt-4">
                {LETTERS.map((l) => (
                  <p key={l} className={`text-sm ${l === detail.question.correct_answer ? "text-success font-semibold" : "text-muted-foreground"}`}>
                    {l}. {detail.question.options[l]}{l === detail.question.correct_answer && " ✓ correct"}
                  </p>
                ))}
              </div>
              <div className="bg-background border border-border rounded-md p-4 mt-4">
                <p className="text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground mb-1.5">Explanation</p>
                <p className="text-sm leading-relaxed">{detail.question.explanation_correct}</p>
                {detail.question.key_learning_point && (
                  <p className="text-sm mt-3"><span className="font-semibold">Key point: </span>{detail.question.key_learning_point}</p>
                )}
                {detail.question.reference && <p className="text-xs text-muted-foreground mt-2">Reference: {detail.question.reference}</p>}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
